import type { AudioSettings } from '../../features/settings/domain/audioSettings';
import { DEFAULT_AUDIO_SETTINGS } from '../../features/settings/domain/audioSettings';

export type SoundCue = 'plant' | 'harvest' | 'coin' | 'build' | 'outfit';

interface AudioWindow extends Window {
  webkitAudioContext?: typeof AudioContext;
}

const cueNotes: Record<SoundCue, Array<[number, number, number]>> = {
  plant: [
    [330, 0, 0.08],
    [440, 0.07, 0.1],
  ],
  harvest: [
    [440, 0, 0.08],
    [554, 0.07, 0.1],
    [659, 0.14, 0.14],
  ],
  coin: [
    [784, 0, 0.07],
    [988, 0.08, 0.13],
  ],
  build: [
    [220, 0, 0.08],
    [330, 0.09, 0.1],
    [440, 0.18, 0.16],
  ],
  outfit: [
    [523, 0, 0.09],
    [659, 0.08, 0.12],
    [784, 0.16, 0.16],
  ],
};

class AudioEngine {
  private context: AudioContext | null = null;
  private musicGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private musicTimer: number | undefined;
  private settings = DEFAULT_AUDIO_SETTINGS;

  public applySettings(settings: AudioSettings): void {
    this.settings = settings;
    if (!this.context) return;
    this.musicGain?.gain.setTargetAtTime(
      settings.musicVolume * 0.18,
      this.context.currentTime,
      0.04,
    );
    this.sfxGain?.gain.setTargetAtTime(settings.sfxVolume * 0.32, this.context.currentTime, 0.02);
    if (settings.musicVolume > 0) this.startMusic();
    else this.stopMusic();
  }

  public async unlock(): Promise<void> {
    if (typeof window === 'undefined') return;
    const AudioContextClass = window.AudioContext ?? (window as AudioWindow).webkitAudioContext;
    if (!AudioContextClass) return;
    if (!this.context) {
      this.context = new AudioContextClass();
      this.musicGain = this.context.createGain();
      this.sfxGain = this.context.createGain();
      this.musicGain.connect(this.context.destination);
      this.sfxGain.connect(this.context.destination);
      this.applySettings(this.settings);
    }
    if (this.context.state === 'suspended') await this.context.resume();
    if (this.settings.musicVolume > 0) this.startMusic();
  }

  public play(cue: SoundCue): void {
    if (this.settings.sfxVolume <= 0) return;
    void this.unlock().then(() => {
      if (!this.context || !this.sfxGain) return;
      const now = this.context.currentTime;
      for (const [frequency, delay, duration] of cueNotes[cue]) {
        this.playTone(frequency, now + delay, duration, this.sfxGain, 'sine', 0.7);
      }
    });
  }

  private startMusic(): void {
    if (
      !this.context ||
      this.context.state !== 'running' ||
      this.musicTimer !== undefined ||
      this.settings.musicVolume <= 0
    )
      return;
    this.playMusicPhrase();
    this.musicTimer = window.setInterval(() => this.playMusicPhrase(), 5600);
  }

  private stopMusic(): void {
    window.clearInterval(this.musicTimer);
    this.musicTimer = undefined;
  }

  private playMusicPhrase(): void {
    if (!this.context || !this.musicGain || this.context.state !== 'running') return;
    const now = this.context.currentTime;
    const phrase: Array<[number, number]> = [
      [261.63, 0],
      [329.63, 0.7],
      [392, 1.4],
      [329.63, 2.2],
    ];
    for (const [frequency, delay] of phrase)
      this.playTone(frequency, now + delay, 0.9, this.musicGain, 'sine', 0.12);
  }

  private playTone(
    frequency: number,
    startAt: number,
    duration: number,
    destination: AudioNode,
    type: OscillatorType,
    volume: number,
  ): void {
    if (!this.context) return;
    const oscillator = this.context.createOscillator();
    const envelope = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, startAt);
    envelope.gain.setValueAtTime(0.0001, startAt);
    envelope.gain.exponentialRampToValueAtTime(volume, startAt + 0.02);
    envelope.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);
    oscillator.connect(envelope);
    envelope.connect(destination);
    oscillator.start(startAt);
    oscillator.stop(startAt + duration + 0.03);
  }
}

export const audioEngine = new AudioEngine();

export function installAudioUnlock(): () => void {
  const unlock = () => void audioEngine.unlock();
  const resumeWhenVisible = () => {
    if (document.visibilityState === 'visible') unlock();
  };
  document.addEventListener('pointerdown', unlock, { capture: true });
  document.addEventListener('keydown', unlock, { capture: true });
  document.addEventListener('visibilitychange', resumeWhenVisible);
  return () => {
    document.removeEventListener('pointerdown', unlock, { capture: true });
    document.removeEventListener('keydown', unlock, { capture: true });
    document.removeEventListener('visibilitychange', resumeWhenVisible);
  };
}
